import {useState} from 'react'

import {
  Select,
  SelectArrow,
  SelectItem,
  SelectLabel,
  SelectPopover,
  SelectProps,
  SelectProvider,
} from '@ariakit/react'
import styled from 'styled-components'
import * as RadixPortal from '@radix-ui/react-portal'
import classNames from 'classnames'

import {styledTheme} from '@festi/common/themes'

import NewCheckbox from './NewCheckbox'

const Label = styled(SelectLabel)`
  max-width: 100%;
  padding-bottom: 8px;
  font-size: 16px;
  font-weight: 500;
  line-height: normal;
  color: ${styledTheme.palette.blue};
  pointer-events: none;
`

const SelectTrigger = styled(Select)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  height: 48px;
  background-color: ${styledTheme.palette.white};
  border: 1px solid ${styledTheme.palette.grey};
  border-radius: 8px;
  padding-inline: 16px;
  padding-block: 13px;
  color: ${styledTheme.palette.blue};
  font-size: 16px;
  font-weight: 500;
  line-height: normal;
  cursor: pointer;
  transition: transform 150ms ease-in-out;

  span {
    color: ${styledTheme.palette.ui30Solid};
  }

  &.open {
    span {
      transform: rotate(180deg);
    }
  }

  &:focus-visible,
  &:hover {
    border-color: ${styledTheme.palette.ui30Solid};
  }
  &:focus {
    outline: none;
  }
`

const Popover = styled(SelectPopover)`
  z-index: ${styledTheme.zIndex.modal};
  background-color: ${styledTheme.palette.white};
  border: 1px solid ${styledTheme.palette.grey};
  border-radius: 8px;
  box-shadow: ${styledTheme.boxShadow.simpleSubtle};
`

const ListItem = styled(SelectItem)`
  display: flex;
  gap: 8px;
  align-items: center;
  background-color: ${styledTheme.palette.white};
  border-bottom: 1px solid ${styledTheme.palette.ui10Solid};
  height: 46px;
  width: 100%;
  padding-inline: 16px;
  padding-block: 12px;
  color: ${styledTheme.palette.blue};
  font-size: 16px;
  font-weight: 400;
  transition: background-color 10ms ease-in dissolve;
  user-select: none;
  cursor: pointer;

  &:first-child {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }

  &:last-child {
    border-bottom: none;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }

  &[data-active-item] {
    background-color: ${styledTheme.palette.primaryLightestBlue};
  }
`

function renderValue(value: string[], placeholder: string) {
  if (value.length === 0) return placeholder
  if (value.length === 1) return value[0]
  return value.map((v) => v).join(', ')
}

interface Props extends SelectProps {
  label: string
  placeholder: string
  items: string[]
  values: string[]
  setValues: (values: string[]) => void
}

export default function MultiSelectDropdown({
  label,
  placeholder,
  items,
  values,
  setValues,
}: Props) {
  const [open, setOpen] = useState(false)

  return (
    <div>
      <SelectProvider
        value={values}
        setValue={setValues}
        open={open}
        setOpen={setOpen}
      >
        <Label>{label}</Label>
        <SelectTrigger className={classNames({open})}>
          {renderValue(values, placeholder)}
          <SelectArrow />
        </SelectTrigger>
        <RadixPortal.Root>
          <Popover gutter={2} sameWidth unmountOnHide>
            {items.map((value) => (
              <ListItem key={value} value={value}>
                <div
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  <NewCheckbox
                    checked={values.includes(value)}
                    value={value}
                    onChange={(e) => {
                      const updatedValues = e.target.checked
                        ? [...values, value]
                        : values.filter((v: string) => v !== value)
                      setValues(updatedValues)
                    }}
                  />
                </div>
                {value}
              </ListItem>
            ))}
          </Popover>
        </RadixPortal.Root>
      </SelectProvider>
    </div>
  )
}
